import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock, CheckCircle, XCircle, Loader2 } from "lucide-react";
import z from "zod";
import {
	resetPasswordMutation,
	validateResetTokenOptions,
} from "@/lib/client/@tanstack/react-query.gen";

export const Route = createFileRoute("/reset-password")({
	component: ResetPasswordPage,
	validateSearch: z.object({
		token: z.string(),
	}),
});

function ResetPasswordPage() {
	const navigate = useNavigate();
	const { token } = Route.useSearch();
	const [passwordReset, setPasswordReset] = useState(false);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const { data: tokenValidation, isLoading } = useQuery({
		...validateResetTokenOptions({
			path: {
				token,
			},
		}),
		enabled: !!token,
	});

	const mutation = useMutation({
		...resetPasswordMutation(),
		onSuccess: () => {
			setPasswordReset(true);
			setTimeout(() => {
				navigate({ to: "/login" });
			}, 3000);
		},
	});

	const form = useForm({
		defaultValues: {
			password: "",
			confirmPassword: "",
		},
		onSubmit: async ({ value }) => {
			if (value.password !== value.confirmPassword) {
				return;
			}
			mutation.mutate({
				body: {
					token,
					newPassword: value.password,
				},
			});
		},
	});

	useEffect(() => {
		setPassword("");
		setConfirmPassword("");
		form.setFieldValue("password", "");
		form.setFieldValue("confirmPassword", "");
	}, [token]);

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
				<Card className="w-full max-w-md">
					<CardContent className="pt-6">
						<div className="flex flex-col items-center space-y-4">
							<Loader2 className="h-8 w-8 animate-spin text-blue-600" />
							<p className="text-gray-600">Validation du lien...</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (!token || !tokenValidation?.valid) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
				<Card className="w-full max-w-md">
					<CardHeader>
						<div className="flex justify-center mb-4">
							<div className="bg-red-100 p-3 rounded-full">
								<XCircle className="w-8 h-8 text-red-600" />
							</div>
						</div>
						<CardTitle className="text-center">Lien invalide</CardTitle>
						<CardDescription className="text-center">
							Ce lien de réinitialisation n'est pas valide ou a expiré.
						</CardDescription>
					</CardHeader>
					<CardFooter>
						<div className="text-sm text-center w-full space-y-2">
							<p>Vous pouvez demander un nouveau lien de réinitialisation.</p>
							<Link
								to="/forgot-password"
								className="text-blue-600 hover:underline block"
							>
								Demander un nouveau lien
							</Link>
						</div>
					</CardFooter>
				</Card>
			</div>
		);
	}

	if (passwordReset) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
				<Card className="w-full max-w-md">
					<CardHeader>
						<div className="flex justify-center mb-4">
							<div className="bg-green-100 p-3 rounded-full">
								<CheckCircle className="w-8 h-8 text-green-600" />
							</div>
						</div>
						<CardTitle className="text-center">
							Mot de passe réinitialisé !
						</CardTitle>
						<CardDescription className="text-center">
							Votre mot de passe a été modifié avec succès. Vous allez être
							redirigé vers la page de connexion...
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<div className="flex justify-center mb-4">
						<div className="bg-blue-100 p-3 rounded-full">
							<Lock className="w-8 h-8 text-blue-600" />
						</div>
					</div>
					<CardTitle className="text-center">Nouveau mot de passe</CardTitle>
					<CardDescription className="text-center">
						Choisissez un nouveau mot de passe pour votre compte
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4"
					>
						<form.Field
							name="password"
							children={(field) => (
								<div className="space-y-2">
									<Label htmlFor="password">Nouveau mot de passe</Label>
									<div className="relative">
										<Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
										<Input
											id="password"
											type="password"
											placeholder="Minimum 8 caractères"
											className="pl-10"
											value={field.state.value}
											onChange={(e) => {
												field.handleChange(e.target.value);
												setPassword(e.target.value);
											}}
											required
											minLength={8}
										/>
									</div>
								</div>
							)}
						/>

						<form.Field
							name="confirmPassword"
							children={(field) => (
								<div className="space-y-2">
									<Label htmlFor="confirmPassword">
										Confirmer le mot de passe
									</Label>
									<div className="relative">
										<Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
										<Input
											id="confirmPassword"
											type="password"
											placeholder="Confirmez votre nouveau mot de passe"
											className="pl-10"
											value={field.state.value}
											onChange={(e) => {
												field.handleChange(e.target.value);
												setConfirmPassword(e.target.value);
											}}
											required
										/>
									</div>
									{confirmPassword &&
										password &&
										confirmPassword !== password && (
											<p className="text-sm text-red-600">
												Les mots de passe ne correspondent pas
											</p>
										)}
								</div>
							)}
						/>

						{mutation.isError && (
							<Alert variant="destructive">
								<AlertDescription>
									Une erreur est survenue. Veuillez réessayer.
								</AlertDescription>
							</Alert>
						)}

						<Button
							type="submit"
							className="w-full"
							disabled={
								mutation.isPending ||
								!password ||
								!confirmPassword ||
								password !== confirmPassword ||
								password.length < 8
							}
						>
							{mutation.isPending
								? "Réinitialisation..."
								: "Réinitialiser le mot de passe"}
						</Button>
					</form>
				</CardContent>
				<CardFooter>
					<div className="text-sm text-center w-full">
						<Link to="/login" className="text-blue-600 hover:underline">
							Retour à la connexion
						</Link>
					</div>
				</CardFooter>
			</Card>
		</div>
	);
}
